/**
 * DesignEthosPanel — "Rex, learn our design ethos".
 *
 * Triggers the VLM extraction pass over APPROVED posts (in-app
 * PUBLISHED/COMPLETED + ingested 'exemplars' folder) and shows the learned
 * house-style profile that now grounds the Auto-Compose agent. Read-only
 * display (the user chose "ground the generator", not an editable profile).
 */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Loader2, Sparkles, Wand2 } from "lucide-react";

const _API_BASE =
  typeof window !== "undefined" && window.location.hostname !== "localhost"
    ? window.location.origin
    : process.env.REACT_APP_BACKEND_URL || "";
const API = `${_API_BASE}/api`;

const CONF_COLOR = { high: "bg-emerald-100 text-emerald-800", medium: "bg-amber-100 text-amber-800", low: "bg-slate-100 text-slate-700" };

function Dist({ title, dist }) {
  if (!dist) return null;
  const entries = Object.entries(dist).sort((a, b) => b[1] - a[1]);
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3" data-testid={`ethos-dist-${title}`}>
      <div className="text-xs font-semibold text-slate-700 mb-1.5 capitalize">{title.replace(/_/g, " ")}</div>
      <div className="space-y-1">
        {entries.map(([k, v]) => (
          <div key={k} className="flex items-center gap-2 text-[11px]">
            <span className="w-24 shrink-0 text-slate-600 truncate">{k}</span>
            <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full bg-indigo-500" style={{ width: `${Math.round(v * 100)}%` }} />
            </div>
            <span className="w-9 text-right text-slate-500 tabular-nums">{Math.round(v * 100)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DesignEthosPanel({ userIsAdmin }) {
  const [wsId, setWsId] = useState(null);
  const [ethos, setEthos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [learning, setLearning] = useState(false);
  const [progress, setProgress] = useState(null); // {done,total}
  const [folderUrl, setFolderUrl] = useState("");
  const [ingesting, setIngesting] = useState(false);
  const [ingestStatus, setIngestStatus] = useState("");

  const loadEthos = useCallback(async (ws) => {
    try {
      const r = await fetch(`${API}/v2/workspaces/${ws}/design-ethos`, { credentials: "include" });
      if (r.ok) setEthos((await r.json()).ethos || null);
    } catch (_) {}
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/v2/workspaces/me`, { credentials: "include" });
        const list = r.ok ? await r.json() : [];
        const ws = Array.isArray(list) && list.length ? list[0].workspace_id : null;
        setWsId(ws);
        if (ws) await loadEthos(ws);
      } finally {
        setLoading(false);
      }
    })();
  }, [loadEthos]);

  const runLearn = async () => {
    if (!wsId || learning) return;
    setLearning(true);
    setProgress(null);
    try {
      const r = await fetch(`${API}/v2/workspaces/${wsId}/design-ethos/learn`, { method: "POST", credentials: "include" });
      const started = await r.json().catch(() => ({}));
      if (!r.ok || !started.job_id) {
        toast.error(started?.detail || "Couldn't start learning");
        setLearning(false); return;
      }
      const deadline = Date.now() + 5 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((res) => setTimeout(res, 3000));
        const sr = await fetch(`${API}/v2/workspaces/${wsId}/design-ethos/learn-status/${started.job_id}`, { credentials: "include" });
        const sj = await sr.json().catch(() => ({}));
        if (sj.total) setProgress({ done: sj.done || 0, total: sj.total });
        if (sj.status === "done") {
          setEthos(sj.ethos || null);
          toast.success(`Rex learned from ${sj.ethos?.sample_size || 0} approved posts`);
          break;
        }
        if (sj.status === "error") {
          toast.error(sj?.detail || "Learning failed");
          break;
        }
      }
    } catch (_) {
      toast.error("Couldn't learn the design ethos right now");
    } finally {
      setLearning(false);
      setProgress(null);
    }
  };

  const linkFolder = async () => {
    if (!wsId || ingesting || !folderUrl.trim()) return;
    setIngesting(true);
    setIngestStatus("Linking folder…");
    try {
      const r = await fetch(`${API}/v2/workspaces/${wsId}/design-ethos/ingest-folder`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ folder_path: folderUrl.trim() }),
      });
      const started = await r.json().catch(() => ({}));
      if (!r.ok || !started.job_id) {
        toast.error(started?.detail || "Couldn't link that folder");
        setIngesting(false); setIngestStatus(""); return;
      }
      const deadline = Date.now() + 5 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((res) => setTimeout(res, 3000));
        const sr = await fetch(`${API}/v2/workspaces/${wsId}/design-ethos/ingest-folder-status/${started.job_id}`, { credentials: "include" });
        const sj = await sr.json().catch(() => ({}));
        if (sj.total) setIngestStatus(`Importing ${sj.done || 0}/${sj.total}…`);
        if (sj.status === "done") {
          toast.success(`Imported ${sj.ingested || 0} finished posts — now click Re-learn`);
          break;
        }
        if (sj.status === "error") {
          toast.error(sj?.detail || "Folder import failed");
          break;
        }
      }
    } catch (_) {
      toast.error("Couldn't link that folder right now");
    } finally {
      setIngesting(false); setIngestStatus("");
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  const dom = ethos?.dominant || {};
  const dist = ethos?.distributions || {};

  return (
    <div className="space-y-5" data-testid="design-ethos-panel">
      <section className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-indigo-900 flex items-center gap-2">
              <Wand2 className="h-5 w-5" /> Design Ethos — learn from approved posts
            </h3>
            <p className="text-xs text-indigo-700/80 mt-1 max-w-2xl">
              Rex studies your APPROVED posts (posted via Buffer + any finished posts ingested from your
              designer's folder) and extracts the house style — cut-outs, subject placement, treatment,
              overlays, text hierarchy and how design serves the message. This profile then grounds the
              "Rex, design this" auto-composer so its drafts mirror your designer.
            </p>
          </div>
          {userIsAdmin && (
            <Button onClick={runLearn} disabled={learning} className="bg-indigo-600 hover:bg-indigo-700 text-white shrink-0" data-testid="design-ethos-learn-btn">
              {learning
                ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />{progress ? `Analysing ${progress.done}/${progress.total}…` : "Starting…"}</>
                : <><Sparkles className="h-4 w-4 mr-1.5" />{ethos ? "Re-learn" : "Learn design ethos"}</>}
            </Button>
          )}
        </div>
        {userIsAdmin && (
          <div className="mt-4 border-t border-indigo-200/70 pt-3">
            <div className="text-[11px] font-semibold text-indigo-900 mb-1.5">
              Link your designer's finished-posts folder (SharePoint)
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={folderUrl}
                onChange={(e) => setFolderUrl(e.target.value)}
                disabled={ingesting}
                placeholder="Paste the SharePoint share link or folder path…"
                className="flex-1 text-xs rounded-md border border-indigo-200 bg-white p-2 text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                data-testid="design-ethos-folder-input"
              />
              <Button onClick={linkFolder} disabled={ingesting || !folderUrl.trim()} variant="outline" className="border-indigo-300 text-indigo-700 shrink-0" data-testid="design-ethos-link-folder-btn">
                {ingesting ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />{ingestStatus || "Linking…"}</> : "Link & import"}
              </Button>
            </div>
            <p className="text-[10px] text-indigo-700/70 mt-1">
              Imports the images into the exemplars pool. Then click "Re-learn" to fold them into the ethos.
            </p>
          </div>
        )}
      </section>

      {!ethos ? (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600" data-testid="design-ethos-empty">
          No design ethos learned yet. Click "Learn design ethos" to analyse your approved posts.
        </div>
      ) : (
        <>
          <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-3" data-testid="design-ethos-summary">
            <div className="flex flex-wrap items-center gap-2">
              <Badge className="bg-indigo-100 text-indigo-800">{ethos.sample_size} posts analysed</Badge>
              <Badge className={CONF_COLOR[ethos.confidence] || CONF_COLOR.low}>{ethos.confidence} confidence</Badge>
              {ethos.sources?.in_app != null && <Badge variant="outline">{ethos.sources.in_app} in-app</Badge>}
              {ethos.sources?.sharepoint != null && <Badge variant="outline">{ethos.sources.sharepoint} from folder</Badge>}
            </div>
            <ul className="text-sm text-slate-700 space-y-1 list-disc pl-5">
              {dom.uses_cutout_subject != null && <li>Cut-out subject: <b>{dist.uses_cutout_subject?.true ? Math.round(dist.uses_cutout_subject.true * 100) : 0}%</b> of posts (avg {ethos.avg_subject_count} when used)</li>}
              {dom.subject_treatment && <li>Subject usually <b>{dom.subject_v}-{dom.subject_h}</b>, treatment <b>{dom.subject_treatment}</b></li>}
              {dom.overlay_strength && <li>Overlay tint usually <b>{dom.overlay_strength}</b></li>}
              {dom.text_anchor && <li>Headline usually anchored <b>{dom.text_anchor}</b>, emphasis <b>{dom.headline_emphasis}</b></li>}
              {dom.layout && <li>Layout most often <b>{dom.layout}</b>; imagery <b>{dom.busyness}</b>, palette on-brand <b>{dom.palette_on_brand}</b></li>}
            </ul>
          </section>

          {!!(ethos.intent_examples || []).length && (
            <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="design-ethos-intents">
              <div className="text-sm font-semibold text-slate-700 mb-2">How design serves the message</div>
              <ul className="text-xs text-slate-600 space-y-1.5 list-disc pl-5">
                {ethos.intent_examples.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </section>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Dist title="subject_treatment" dist={dist.subject_treatment} />
            <Dist title="overlay_strength" dist={dist.overlay_strength} />
            <Dist title="text_anchor" dist={dist.text_anchor} />
            <Dist title="layout" dist={dist.layout} />
          </div>
        </>
      )}
    </div>
  );
}
